import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API } from "../App";

/**
 * Hook: given an array of prediction items (each with match.home_team,
 * match.away_team, match.league), returns a Map keyed by
 * `${fixture_id}` → true / false / null (null = unknown).
 *
 * Batches one POST /api/betway/availability/check call per predictions
 * change, caches results for the life of the component.  Silently
 * no-ops if the endpoint isn't reachable.
 */
export const useBetwayAvailability = (predictions) => {
  const [availability, setAvailability] = useState({});
  const lastKeyRef = useRef(null);

  useEffect(() => {
    if (!predictions || predictions.length === 0) return;
    // Build a stable key so we don't re-call unless the set changes
    const key = predictions
      .map((p) => `${p?.match?.fixture_id || p?.match?.id}`)
      .join(",");
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;

    const matches = predictions
      .map((p) => ({
        fixture_id: p?.match?.fixture_id || p?.match?.id,
        home_team: p?.match?.home_team?.name,
        away_team: p?.match?.away_team?.name,
        league: p?.match?.league?.name,
        country: p?.match?.league?.country,
      }))
      .filter((m) => m.home_team && m.away_team);

    if (matches.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/betway/availability/check`,
          { matches: matches.map(({ fixture_id, ...rest }) => rest) }
        );
        if (cancelled) return;
        const map = {};
        (data.results || []).forEach((val, idx) => {
          const m = matches[idx];
          if (m?.fixture_id != null) map[String(m.fixture_id)] = val;
        });
        setAvailability(map);
      } catch (_) {
        // Endpoint missing / network error — fail silent, just no badges
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [predictions]);

  return availability;
};
